import { space, SpaceBetween, tok } from '@sunwebgroup/ui-components'

import { Button, Icon, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'

import { use, useState } from 'react'

import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { routesSelector } from '@/scopes/prices/components/selectors'
import { FlightCard } from '@/scopes/prices/components/TransportSelector/FlightSelector/FlightCard'
import { FlightCardNew } from '@/scopes/prices/components/TransportSelector/FlightSelector/FlightSelectorNew/FlightCardNew'
import { ArrivalTitle, DepartureTitle } from '@/scopes/prices/components/TransportSelector/FlightSelector/Headers'
import { createFlightComponentInteractionEvent } from '@/scopes/prices/domain/analytics'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

const Section = styled(SpaceBetween)`
  outline: none;
  flex-basis: 100%;
  margin-bottom: ${space('primitives_space_16')};
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SheetCustomHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  height: 48px;
  box-sizing: border-box;
  padding: 0 ${space('primitives_space_16')};
  border-bottom: 2px solid ${tok('semantics_dividers_color')};
  background-color: ${tok('primitives_color_neutral_0')};
  position: relative;
`
const ReturnWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`
const STEPS = {
  selectDeparture: 'selectDeparture',
  selectReturn: 'selectReturn',
}

const FlightSelectorTitle = styled(Typography)`
  display: block;
`
const FlightSelectorContainer = styled.div`
  border: solid ${tok('primitives_color_neutral_100')};
  border-width: 2px 0 0 0;
  padding: ${space('primitives_space_16')};
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_16')};
`

const SheetTitle = styled(Typography)`
  padding-top: ${space('primitives_space_20')};
`

const FlightCardWrapper = styled.div`
  padding-top: ${space('primitives_space_16')};
`

const HeaderCell = styled.div<{ alignment?: 'flex-start' | 'center' | 'flex-end' }>`
  display: flex;
  flex-basis: 33%;
  justify-content: ${({ alignment }) => alignment || 'flex-start'};
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${space('primitives_space_16')} ${space('primitives_space_32')} ${space('primitives_space_16')} 0;
  bottom: 0;
  position: fixed;
  background-color: ${tok('primitives_color_neutral_0')};
  width: 100%;
  border-top: 2px solid ${tok('semantics_dividers_color')};
`
const SheetContent = styled(Sheet.Content)`
  padding-bottom: ${space('primitives_space_96')} !important;
  overflow-y: auto;
`
export function FlightSelectorMobile() {
  const { enableAlternateFlightVisualisation } = useOptimizelyFlagContext()
  const Card = enableAlternateFlightVisualisation ? FlightCardNew : FlightCard
  const intl = useIntl()
  const {
    setInboundRouteId,
    setOutboundRouteId,
  } = use(PricesActionsContext)

  const {
    outboundRoute,
    inboundRoute,
    inboundRoutes,
    outboundRoutes,
    selectedOutboundRouteId,
    selectedInboundRouteId,
  } = usePricesSelector(routesSelector)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [step, setStep] = useState<string | null>(null)
  const { sendEvent } = useAnalytics()
  const sheetContent
    = step === STEPS.selectDeparture
      ? `${intl.formatMessage({ id: 'prices_transport_selector_flight-selector_select_your_flight_outward' })} ${
        outboundRoute?.departureDate.fullDate
      }`
      : `${intl.formatMessage({ id: 'prices_transport_selector_flight-selector_select_your_flight_inward' })} ${
        inboundRoute?.arrivalDate.fullDate
      }`
  if (isEmpty(outboundRoutes) || isEmpty(inboundRoutes))
    return null

  const handleOpenModal = () => {
    setIsModalOpen(true)
    setStep(STEPS.selectDeparture)
    sendAnalyticsEvent('outbound')
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setStep(null)
  }

  function changeStep(text: string) {
    setStep(text)
    sendAnalyticsEvent(text === STEPS.selectDeparture ? 'outbound' : 'inbound')
  }

  function sendAnalyticsEvent(type: string) {
    sendEvent(createFlightComponentInteractionEvent({ type, result: 'Change flight' }))
  }

  const selectOutboundRoute = (id: string) => {
    setOutboundRouteId(id)
  }

  const selectInboundRoute = (id: string) => {
    setInboundRouteId(id)
  }

  return (
    <>
      <FlightSelectorContainer>
        <FlightSelectorTitle variant="display-f-regular">
          {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_title' })}
        </FlightSelectorTitle>
        <Wrapper id="FlightSelector">
          <Section direction="y">
            <DepartureTitle />
            {outboundRoute && <Card {...outboundRoute} isSelected noActiveState />}
          </Section>
          <Section direction="y">
            <ArrivalTitle />
            {inboundRoute && <Card {...inboundRoute} isSelected noActiveState />}
          </Section>
          <Button variant="secondary" onClick={handleOpenModal}>
            {intl.formatMessage({
              id: 'prices_transport_selector_flight-selector-mobile_button',
            })}
          </Button>
        </Wrapper>
        <Sheet.Root open={isModalOpen} onOpenChange={handleCloseModal}>
          <Sheet.Body side="right">
            <SheetCustomHeader>
              <HeaderCell>
                {step === STEPS.selectReturn && (
                  <ReturnWrapper onClick={() => changeStep(STEPS.selectDeparture)}>
                    <Icon glyph="ChevronLeft" size={20}></Icon>
                    <Typography variant="body-m-bold-tight">
                      {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_outward' })}
                    </Typography>
                  </ReturnWrapper>
                )}
              </HeaderCell>
              <HeaderCell alignment="center">
                <Typography variant="body-m-bold-tight">
                  {step === STEPS.selectDeparture
                    ? intl.formatMessage({ id: 'prices_transport_selector_flight-selector_outward-flight' })
                    : intl.formatMessage({ id: 'prices_transport_selector_flight-selector-inward-flight' })}
                </Typography>
              </HeaderCell>
              <HeaderCell alignment="flex-end">
                <Button variant="transparentPrimary" onClick={handleCloseModal}>
                  {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_cancel' })}
                </Button>
              </HeaderCell>
            </SheetCustomHeader>
            <SheetContent>
              <SheetTitle variant="body-m-regular-snug">{sheetContent}</SheetTitle>
              {step === STEPS.selectDeparture && (
                <>
                  {outboundRoutes.map(route => (
                    <FlightCardWrapper key={route.id}>
                      <Card
                        {...route}
                        setSelected={selectOutboundRoute}
                        isSelected={selectedOutboundRouteId === route.id}
                      />
                    </FlightCardWrapper>
                  ))}
                </>
              )}
              {step === STEPS.selectReturn && (
                <>
                  {inboundRoutes.map(route => (
                    <FlightCardWrapper key={route.id}>
                      <Card
                        {...route}
                        setSelected={selectInboundRoute}
                        isSelected={selectedInboundRouteId === route.id}
                      />
                    </FlightCardWrapper>
                  ))}
                </>
              )}
              <ButtonWrapper>
                {step === STEPS.selectDeparture && (
                  <Button onClick={() => changeStep(STEPS.selectReturn)} fullWidth>
                    {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_save_and_continue' })}
                  </Button>
                )}
                {step === STEPS.selectReturn && (
                  <Button onClick={handleCloseModal} fullWidth>
                    {intl.formatMessage({ id: 'prices_transport_selector_flight-selector-save' })}
                  </Button>
                )}
              </ButtonWrapper>
            </SheetContent>
          </Sheet.Body>
        </Sheet.Root>
      </FlightSelectorContainer>
    </>
  )
}
